import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Inbox, Phone, Mail, Building2, Calendar, MessageSquare, CheckCircle2,
  Clock, XCircle, MailOpen, Trash2, Loader2,
} from 'lucide-react';
import apiClient from '@/services/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type DemoRequest = {
  id: string;
  name: string;
  email: string;
  firm: string;
  phone: string | null;
  firm_size: string;
  time_preference: string;
  notes: string | null;
  status: 'new' | 'contacted' | 'scheduled' | 'completed' | 'declined';
  admin_notes: string | null;
  contacted_at: string | null;
  created_at: string;
  updated_at: string;
};

type DemoRequestsResponse = {
  requests: DemoRequest[];
  counts: Record<string, number>;
};

const SIZE_LABELS: Record<string, string> = {
  solo: 'Solo / 1–10',
  small: '10–50',
  medium: '50–200',
  large: '200–1000',
  enterprise: '1000+',
};
const TIME_LABELS: Record<string, string> = {
  'this-week': 'This week',
  'next-week': 'Next week',
  'flexible': 'Flexible',
};

const STATUS_OPTIONS: { key: DemoRequest['status']; label: string; icon: any; tone: string }[] = [
  { key: 'new', label: 'New', icon: Inbox, tone: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  { key: 'contacted', label: 'Contacted', icon: MailOpen, tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  { key: 'scheduled', label: 'Scheduled', icon: Calendar, tone: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30' },
  { key: 'completed', label: 'Completed', icon: CheckCircle2, tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  { key: 'declined', label: 'Declined', icon: XCircle, tone: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30' },
];

export default function DemoRequestsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<DemoRequest['status'] | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-demo-requests'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: DemoRequestsResponse }>('/admin/demo-requests');
      return r.data.data;
    },
    refetchInterval: 60_000,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status, adminNotes }: { id: string; status?: string; adminNotes?: string }) => {
      const r = await apiClient.patch(`/admin/demo-requests/${id}`, { status, adminNotes });
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-demo-requests'] });
      toast.success('Updated');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error || 'Update failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/demo-requests/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-demo-requests'] });
      toast.success('Deleted');
    },
  });

  const requests = data?.requests || [];
  const counts = data?.counts || {};
  const visible = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const totalCount = requests.length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Inbox}
        title="Demo requests"
        description="Marketing demo requests from the public site. Reach out within one business day."
      />

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        <FilterChip
          label="All"
          count={totalCount}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        {STATUS_OPTIONS.map(opt => {
          const Icon = opt.icon;
          return (
            <FilterChip
              key={opt.key}
              label={opt.label}
              count={counts[opt.key] || 0}
              active={filter === opt.key}
              onClick={() => setFilter(opt.key)}
              icon={Icon}
            />
          );
        })}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : visible.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Inbox className="h-10 w-10 text-muted-foreground/50" />
            <p className="mt-4 text-sm font-medium">
              {filter === 'all' ? 'No demo requests yet' : `No ${filter} requests`}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              When prospects book a demo, they'll appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {visible.map(req => (
            <RequestRow
              key={req.id}
              req={req}
              expanded={expandedId === req.id}
              onToggle={() => setExpandedId(expandedId === req.id ? null : req.id)}
              onUpdate={(patch) => updateMutation.mutate({ id: req.id, ...patch })}
              onDelete={() => {
                if (confirm(`Delete demo request from ${req.name}? This cannot be undone.`)) {
                  deleteMutation.mutate(req.id);
                }
              }}
              isUpdating={updateMutation.isPending && updateMutation.variables?.id === req.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({
  label, count, active, onClick, icon: Icon,
}: { label: string; count: number; active: boolean; onClick: () => void; icon?: any }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-card text-foreground hover:border-muted-foreground/30'
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
      <span className={cn(
        'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold',
        active ? 'bg-primary-foreground/20' : 'bg-muted'
      )}>{count}</span>
    </button>
  );
}

function RequestRow({
  req, expanded, onToggle, onUpdate, onDelete, isUpdating,
}: {
  req: DemoRequest;
  expanded: boolean;
  onToggle: () => void;
  onUpdate: (patch: { status?: string; adminNotes?: string }) => void;
  onDelete: () => void;
  isUpdating: boolean;
}) {
  const [adminNotes, setAdminNotes] = useState(req.admin_notes || '');
  const tone = STATUS_OPTIONS.find(o => o.key === req.status)?.tone || '';
  const created = new Date(req.created_at);
  const ageMin = Math.round((Date.now() - created.getTime()) / 60_000);
  const age = ageMin < 60 ? `${ageMin}m ago` : ageMin < 1440 ? `${Math.round(ageMin / 60)}h ago` : `${Math.round(ageMin / 1440)}d ago`;

  return (
    <Card>
      <CardContent className="p-0">
        <button onClick={onToggle} className="w-full text-left p-4 hover:bg-muted/30 transition-colors">
          <div className="flex items-start gap-4">
            {/* Status pill */}
            <div className="flex-shrink-0 mt-0.5">
              <span className={cn('inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider', tone)}>
                {req.status}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <p className="text-base font-semibold text-foreground truncate">{req.firm}</p>
                <span className="text-xs text-muted-foreground">·</span>
                <p className="text-sm text-muted-foreground truncate">{req.name}</p>
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3" /> {req.email}
                </span>
                {req.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {req.phone}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> {SIZE_LABELS[req.firm_size] || req.firm_size}
                </span>
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <p className="text-xs font-medium text-muted-foreground">{age}</p>
              <Badge variant="outline" className="mt-1 text-[10px]">
                {TIME_LABELS[req.time_preference] || req.time_preference}
              </Badge>
            </div>
          </div>

          {req.notes && !expanded && (
            <p className="mt-3 ml-[88px] text-xs text-muted-foreground line-clamp-1 italic">
              "{req.notes}"
            </p>
          )}
        </button>

        {expanded && (
          <div className="border-t border-border p-4 space-y-4 bg-muted/20">
            {req.notes && (
              <div>
                <p className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <MessageSquare className="h-3 w-3" /> Notes from prospect
                </p>
                <p className="rounded-lg border border-border bg-card p-3 text-sm leading-relaxed whitespace-pre-wrap">
                  {req.notes}
                </p>
              </div>
            )}

            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Update status
              </p>
              <div className="flex flex-wrap gap-1.5">
                {STATUS_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  const active = req.status === opt.key;
                  return (
                    <button
                      key={opt.key}
                      disabled={isUpdating || active}
                      onClick={() => onUpdate({ status: opt.key })}
                      className={cn(
                        'inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
                        active
                          ? `${opt.tone} cursor-default`
                          : 'border-border bg-card text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground'
                      )}
                    >
                      <Icon className="h-3 w-3" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Internal notes
              </p>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Notes for your team — not visible to the prospect."
                rows={3}
                className="resize-none text-sm"
              />
              <div className="mt-2 flex items-center justify-between">
                <p className="text-[11px] text-muted-foreground">
                  Submitted {created.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}
                  {req.contacted_at && ` · contacted ${new Date(req.contacted_at).toLocaleDateString('en-AU')}`}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onDelete}
                    className="text-rose-600 hover:text-rose-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    disabled={adminNotes === (req.admin_notes || '')}
                    onClick={() => onUpdate({ adminNotes })}
                  >
                    {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save notes'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <Button asChild size="sm" variant="outline">
                <a href={`mailto:${req.email}?subject=RoniDoc%20demo%20for%20${encodeURIComponent(req.firm)}`}>
                  <Mail className="h-3.5 w-3.5 mr-1.5" /> Reply by email
                </a>
              </Button>
              {req.phone && (
                <Button asChild size="sm" variant="outline">
                  <a href={`tel:${req.phone}`}>
                    <Phone className="h-3.5 w-3.5 mr-1.5" /> Call
                  </a>
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
